import { useState, type CSSProperties, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  api,
  currentTokens,
  embedImageUrl,
  keys,
  type ActionRow,
  type BotEmbed,
  type ButtonComponent,
  type ComponentEmoji,
  type Member,
  type Message,
  type Role,
  type SelectComponent,
  type SelectOption,
  type Uuid,
} from "@shared/api";
import { byRank } from "@entities/role";
import { useCatalogue } from "@entities/workspace";
import type { CustomEmoji } from "@shared/lib/emoji";
import { FileImage } from "@shared/ui/FileImage";
import { IconCheck, IconChevronDown, IconExternal } from "@shared/ui/icons";
import { Popover, PopoverContent, PopoverTrigger } from "@shared/ui/popover";
import { Spinner } from "@shared/ui/spinner";
import { useInteractions } from "../model/interactions";

/**
 * Вставки и компоненты сообщений бота — как в Discord (resources/message, Embed
 * Object; components/reference). Вставка — карточка с полосой цвета, автором,
 * заголовком, текстом, полями в строку и картинками. Компоненты — ряды кнопок и
 * списков: нажатие уходит боту, на компоненте крутится колесо, пока он не
 * ответил, а через три секунды без ответа — «Ошибка взаимодействия».
 *
 * Картинки вставок идут через наш сервер, как превью ссылок: сайт не узнаёт,
 * кто читает переписку.
 */
export function BotEmbeds({ message, markup }: { message: Message; markup: (text: string) => ReactNode }) {
  if (!message.embeds?.length) return null;
  return (
    <div className="embeds">
      {message.embeds.map((embed, index) => (
        <Embed key={index} embed={embed} messageId={message.id} markup={markup} />
      ))}
    </div>
  );
}

function Embed({
  embed,
  messageId,
  markup,
}: {
  embed: BotEmbed;
  messageId: Uuid;
  markup: (text: string) => ReactNode;
}) {
  const color = embed.color !== undefined ? `#${embed.color.toString(16).padStart(6, "0")}` : undefined;
  return (
    <div className="embed bot-embed" style={color ? ({ "--embed-color": color } as CSSProperties) : undefined}>
      <div className="bot-embed-grid">
        <div className="bot-embed-main">
          {embed.author && (
            <div className="bot-embed-author">
              {embed.author.icon_url && <EmbedPicture url={embed.author.icon_url} messageId={messageId} className="bot-embed-icon" />}
              {embed.author.url ? (
                <a href={embed.author.url} target="_blank" rel="noreferrer noopener">
                  {embed.author.name}
                </a>
              ) : (
                <span>{embed.author.name}</span>
              )}
            </div>
          )}
          {embed.title &&
            (embed.url ? (
              <a className="embed-title" href={embed.url} target="_blank" rel="noreferrer noopener">
                {embed.title}
              </a>
            ) : (
              <div className="embed-title">{embed.title}</div>
            ))}
          {embed.description && <div className="bot-embed-text">{markup(embed.description)}</div>}
          {embed.fields && embed.fields.length > 0 && (
            <div className="bot-embed-fields">
              {embed.fields.map((field, index) => (
                <div key={index} className={`bot-embed-field${field.inline ? " inline" : ""}`}>
                  <div className="bot-embed-field-name">{markup(field.name)}</div>
                  <div className="bot-embed-field-value">{markup(field.value)}</div>
                </div>
              ))}
            </div>
          )}
        </div>
        {embed.thumbnail && <EmbedPicture url={embed.thumbnail.url} messageId={messageId} className="bot-embed-thumb" />}
      </div>
      {embed.image && <EmbedPicture url={embed.image.url} messageId={messageId} className="bot-embed-image" />}
      {(embed.footer || embed.timestamp) && (
        <div className="bot-embed-footer">
          {embed.footer?.icon_url && <EmbedPicture url={embed.footer.icon_url} messageId={messageId} className="bot-embed-icon" />}
          <span className="ellipsis">
            {[embed.footer?.text, embed.timestamp && new Date(embed.timestamp).toLocaleString("ru-RU", { dateStyle: "short", timeStyle: "short" })]
              .filter(Boolean)
              .join(" • ")}
          </span>
        </div>
      )}
    </div>
  );
}

/** Картинка вставки — через наш сервер, по токену; не отдал — её нет. */
function EmbedPicture({ url, messageId, className }: { url: string; messageId: Uuid; className: string }) {
  const data = useProxiedImage(url, messageId);
  if (!data) return null;
  return <img className={className} src={data} alt="" loading="lazy" />;
}

/**
 * Картинка вставки бота — через наш прокси, ссылкой на blob. Прокси отдаёт её,
 * только если она есть во вставке этого сообщения. Не отдалось — `null`.
 */
function useProxiedImage(url: string | null | undefined, messageId: Uuid): string | null {
  const { data } = useQuery({
    queryKey: keys.embedImage(url ?? ""),
    queryFn: async () => {
      const token = currentTokens().access;
      const response = await fetch(embedImageUrl(url!, messageId), {
        headers: token ? { authorization: `Bearer ${token}` } : {},
      });
      if (!response.ok) return null;
      return URL.createObjectURL(await response.blob());
    },
    enabled: !!url,
    staleTime: Infinity,
    retry: false,
  });
  return data ?? null;
}

/** Значок компонента: свой сервера — картинкой, юникод — знаком. */
function Emoji({ emoji, emojiOf }: { emoji: ComponentEmoji; emojiOf: (name: string) => CustomEmoji | null }) {
  const custom = emoji.id && emoji.name ? emojiOf(emoji.name) : null;
  if (custom) return <FileImage id={custom.file_id} alt={`:${custom.name}:`} className="bot-part-emoji" />;
  return emoji.name && !emoji.id ? <span className="bot-part-emoji">{emoji.name}</span> : null;
}

/**
 * Ряды кнопок и списков под сообщением бота. У эфемерного — те же, нажимает их
 * только тот, кому сообщение показано (сервер это и так знает).
 */
export function BotComponents({
  message,
  emojiOf,
}: {
  message: Message;
  emojiOf: (name: string) => CustomEmoji | null;
}) {
  const presses = useInteractions((state) => state.presses);
  if (!message.components?.length) return null;
  const mine = Object.values(presses).filter((press) => press.messageId === message.id);
  const busy = new Set(mine.filter((press) => press.state === "sent").map((press) => press.customId));
  const failed = mine.some((press) => press.state === "failed");

  const press = async (customId: string, values: string[] = []) => {
    try {
      const { id } = await api.commands.press(message.channel_id, message.id, customId, values);
      useInteractions.getState().pressed({ id, messageId: message.id, customId, channelId: message.channel_id, bot: message.author });
    } catch {
      // Отказ сервера (выключено, не тот выбор) — то же «не вышло», что молчание бота.
      useInteractions.setState((state) => ({
        presses: {
          ...state.presses,
          [`refused-${message.id}`]: {
            id: `refused-${message.id}`,
            messageId: message.id,
            customId,
            channelId: message.channel_id,
            bot: message.author,
            at: Date.now(),
            state: "failed",
          },
        },
      }));
    }
  };

  return (
    <div className="bot-rows">
      {message.components.map((row: ActionRow, index) => (
        <div key={row.id ?? index} className="bot-row">
          {row.components.map((component, place) =>
            component.type === 2 ? (
              <BotButton
                key={component.custom_id ?? component.url ?? place}
                button={component}
                busy={!!component.custom_id && busy.has(component.custom_id)}
                emojiOf={emojiOf}
                onPress={() => component.custom_id && void press(component.custom_id)}
              />
            ) : (
              <BotSelect
                key={component.custom_id}
                select={component}
                channelId={message.channel_id}
                busy={busy.has(component.custom_id)}
                emojiOf={emojiOf}
                onChoose={(values) => void press(component.custom_id, values)}
              />
            ),
          )}
        </div>
      ))}
      {failed && <div className="bot-failed">Ошибка взаимодействия</div>}
    </div>
  );
}

const BUTTON_STYLE = { 1: "primary", 2: "secondary", 3: "success", 4: "danger", 5: "link" } as const;

function BotButton({
  button,
  busy,
  emojiOf,
  onPress,
}: {
  button: ButtonComponent;
  busy: boolean;
  emojiOf: (name: string) => CustomEmoji | null;
  onPress: () => void;
}) {
  const face = (
    <>
      {busy ? <Spinner size={14} /> : button.emoji && <Emoji emoji={button.emoji} emojiOf={emojiOf} />}
      {button.label && <span className="ellipsis">{button.label}</span>}
      {button.style === 5 && <IconExternal size={13} />}
    </>
  );
  const className = `bot-button ${BUTTON_STYLE[button.style]}`;
  if (button.style === 5 && button.url) {
    return button.disabled ? (
      <span className={`${className} disabled`} aria-disabled>
        {face}
      </span>
    ) : (
      <a className={className} href={button.url} target="_blank" rel="noreferrer noopener">
        {face}
      </a>
    );
  }
  return (
    <button type="button" className={className} disabled={button.disabled || busy} onClick={onPress}>
      {face}
    </button>
  );
}

/**
 * Что предлагает список людей (5), ролей (6), тех и других (7) или каналов (8):
 * участники и роли сервера канала, его текстовые и голосовые каналы — с
 * фильтром типов бота. Значения — наши id, сервер сверит их ещё раз.
 */
export function useEntityOptions(select: SelectComponent, channelId: Uuid): SelectOption[] {
  const serverId = useCatalogue(
    (state) => Object.values(state.states).find((server) => server?.channels.some((channel) => channel.id === channelId))?.id,
  );
  const people = select.type === 5 || select.type === 7;
  const ranked = select.type === 6 || select.type === 7;
  // Ключи и вид — те же, что у окна нового канала: состав уже может быть в кэше.
  const { data: members = [] } = useQuery({
    queryKey: keys.members(serverId ?? ""),
    queryFn: () =>
      api.servers
        .members(serverId!)
        .then((value) => value.items)
        .catch(() => [] as Member[]),
    enabled: !!serverId && people,
  });
  const { data: roles = [] } = useQuery({
    queryKey: keys.roles(serverId ?? ""),
    queryFn: () =>
      api.roles
        .list(serverId!)
        .then((value) => byRank(value.items))
        .catch(() => [] as Role[]),
    enabled: !!serverId && ranked,
  });
  const channels = useCatalogue((state) => (serverId ? state.states[serverId]?.channels : undefined));

  if (select.type === 3) return select.options ?? [];
  const defaults = new Set((select.default_values ?? []).map((item) => item.id));
  const options: SelectOption[] = [];
  if (people)
    for (const member of members)
      options.push({
        label: member.display_name || member.username,
        value: member.id,
        description: `@${member.handle}`,
        default: defaults.has(member.id),
      });
  if (ranked)
    for (const role of roles.filter((role) => !role.is_default))
      options.push({ label: `@${role.name}`, value: role.id, default: defaults.has(role.id) });
  if (select.type === 8)
    for (const channel of channels ?? []) {
      // Типы каналов Discord: 0 — текстовый, 2 — голосовой, 5 — объявлений, 13 — сцена, 15 — форум.
      const kind = { text: 0, voice: 2, announcement: 5, stage: 13, forum: 15 }[channel.channel_type as string] ?? 0;
      if (select.channel_types?.length && !select.channel_types.includes(kind)) continue;
      options.push({ label: `#${channel.name}`, value: channel.id, default: defaults.has(channel.id) });
    }
  return options;
}

/**
 * Список бота. Один выбор уходит сразу; несколько — когда список закрыли, как
 * в Discord. Списки людей, ролей и каналов берут их из сервера канала.
 */
function BotSelect({
  select,
  channelId,
  busy,
  emojiOf,
  onChoose,
}: {
  select: SelectComponent;
  channelId: Uuid;
  busy: boolean;
  emojiOf: (name: string) => CustomEmoji | null;
  onChoose: (values: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const all = useEntityOptions(select, channelId);
  // Людей и каналов бывает больше, чем влезает: поиск, как у Discord, и первые 25.
  const wanted = query.trim().toLowerCase();
  const options = (
    wanted
      ? all.filter((option) => `${option.label} ${option.description ?? ""}`.toLowerCase().includes(wanted))
      : all
  ).slice(0, 25);
  // Выбранное по умолчанию — из описания: участники и каналы доезжают позже.
  const initial =
    select.type === 3
      ? all.filter((option) => option.default).map((option) => option.value)
      : (select.default_values ?? []).map((item) => item.id);
  const [chosen, setChosen] = useState<string[]>(initial);
  const many = (select.max_values ?? 1) > 1;
  const min = select.min_values ?? 1;
  const max = select.max_values ?? 1;
  const label = chosen.length
    ? all
        .filter((option) => chosen.includes(option.value))
        .map((option) => option.label)
        .join(", ")
    : (select.placeholder ?? "Выберите");

  const close = (next: boolean) => {
    setOpen(next);
    if (!next && many && chosen.length >= min && chosen.join() !== initial.join()) onChoose(chosen);
  };

  return (
    <Popover open={open} onOpenChange={close}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={`bot-select${chosen.length ? "" : " bot-select-placeholder"}`}
          disabled={select.disabled || busy || (select.type === 3 && all.length === 0)}
        >
          <span className="grow ellipsis">{label}</span>
          {busy ? <Spinner size={14} /> : <IconChevronDown size={15} />}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="bot-select-panel">
        {select.type !== 3 && (
          <input
            className="bot-select-search"
            value={query}
            placeholder="Поиск"
            autoFocus
            onChange={(event) => setQuery(event.target.value)}
          />
        )}
        <div role="listbox" aria-multiselectable={many}>
          {options.length === 0 && <div className="bot-option-none">Ничего не нашлось</div>}
          {options.map((option) => {
            const on = chosen.includes(option.value);
            return (
              <button
                key={option.value}
                type="button"
                role="option"
                aria-selected={on}
                className={`bot-option${on ? " on" : ""}`}
                disabled={!on && many && chosen.length >= max}
                onClick={() => {
                  if (!many) {
                    setChosen([option.value]);
                    setOpen(false);
                    onChoose([option.value]);
                    return;
                  }
                  setChosen(on ? chosen.filter((value) => value !== option.value) : [...chosen, option.value]);
                }}
              >
                {option.emoji && <Emoji emoji={option.emoji} emojiOf={emojiOf} />}
                <span className="grow">
                  <span className="bot-option-label">{option.label}</span>
                  {option.description && <span className="bot-option-note">{option.description}</span>}
                </span>
                {on && <IconCheck size={15} />}
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
